import { Fragment, useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/webpack";
import CfLoader from "../../common/CfLoader/CfLoader";
import { Dialog, Transition } from "@headlessui/react";
import { X } from "lucide-react";

export const TwoPagePdfViewer = ({
  pdfUrl,
  showZoom = true,
  heightClassName = "max-h-[80vh]",
  onRenderComplete,
  onPagesRendered,
  viewerClassName = "",
  paged = false,
}) => {
  const [pageImages, setPageImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const onRenderCompleteRef = useRef(onRenderComplete);
  const onPagesRenderedRef = useRef(onPagesRendered);

  useEffect(() => {
    onRenderCompleteRef.current = onRenderComplete;
  }, [onRenderComplete]);

  useEffect(() => {
    onPagesRenderedRef.current = onPagesRendered;
  }, [onPagesRendered]);

  useEffect(() => {
    if (!pdfUrl) {
      setPageImages([]);
      setIsLoading(false);
      return;
    }

    let isCancelled = false;

    const renderPdfPages = async () => {
      try {
        setIsLoading(true);
        setPageImages([]);

        const response = await fetch(pdfUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.status}`);
        }
        const pdfData = await response.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;

        const renderedPages = [];
        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          const page = await pdf.getPage(pageNumber);
          const viewport = page.getViewport({ scale: 2 });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          const ctx = canvas.getContext("2d");
          await page.render({ canvasContext: ctx, viewport }).promise;
          renderedPages.push(canvas.toDataURL("image/png"));
        }

        if (!isCancelled) {
          setPageImages(renderedPages);
          onRenderCompleteRef.current?.();
          onPagesRenderedRef.current?.(renderedPages.length);
        }
      } catch (error) {
        console.error("Error rendering PDF:", error);
        if (!isCancelled) {
          onRenderCompleteRef.current?.();
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    renderPdfPages();

    return () => {
      isCancelled = true;
    };
  }, [pdfUrl]);

  const renderPagedPages = () =>
    pageImages.map((pageImage, index) => (
      <div
        key={`pdf-page-${index + 1}`}
        className="flex h-full min-h-full w-full max-w-full shrink-0 snap-start snap-always flex-col"
      >
        <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden">
          <img
            src={pageImage}
            alt={`PDF page ${index + 1}`}
            className="max-h-full max-w-full object-contain"
          />
        </div>
        <p className="shrink-0 select-none bg-gray-100 py-1 text-center text-xs text-gray-400">
          Page {index + 1} of {pageImages.length}
        </p>
      </div>
    ));

  const renderStackedPages = (
    extraClassName = "",
    imageClassName = "max-w-full w-full h-auto border border-gray-300 bg-white",
  ) => (
    <div className={`flex w-full max-w-full flex-col ${extraClassName}`}>
      {pageImages.map((pageImage, index) => (
        <div
          key={`pdf-page-${index + 1}`}
          className="flex w-full max-w-full flex-col"
        >
          <img
            src={pageImage}
            alt={`PDF page ${index + 1}`}
            className={imageClassName}
          />
          <p className="select-none bg-gray-100 py-1 text-center text-xs text-gray-400">
            Page {index + 1} of {pageImages.length}
          </p>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div
        className={`relative h-full min-w-0 max-w-full overflow-hidden rounded-lg ${viewerClassName}`}
      >
        <div
          className={`${heightClassName} min-w-0 max-w-full overflow-auto ${
            paged ? "snap-y snap-mandatory overscroll-contain" : ""
          }`}
        >
          {isLoading ? (
            <div className="flex h-full items-center justify-center py-8">
              <CfLoader />
            </div>
          ) : paged ? (
            renderPagedPages()
          ) : (
            renderStackedPages()
          )}
        </div>
      </div>

      <Transition appear show={showZoom && isPreviewModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-20"
          onClose={() => setIsPreviewModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 top-20 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 top-20 overflow-y-auto px-4 md:px-0 max-h-full">
            <div className="flex items-center min-h-full justify-center text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-3xl max-h-[90vh] transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                  <div className="relative px-6 py-4 border-b border-gray-200">
                    <button
                      type="button"
                      className="absolute top-3 right-3 p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                      onClick={() => setIsPreviewModalOpen(false)}
                      aria-label="Close preview"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="max-h-[80vh] overflow-auto px-4">
                    {isLoading ? (
                      <div className="flex justify-center items-center w-full h-[50vh]">
                        <CfLoader />
                      </div>
                    ) : (
                      renderStackedPages(
                        "pt-2 pb-6",
                        "w-auto h-auto max-w-full max-h-[70vh] border border-gray-300 bg-white",
                      )
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};
